import { gql } from "@apollo/client";
import { Typography } from "@material-ui/core";
import { useLogEvent } from "analytics";
import { useAuthContext } from "auth/context";
import DropzoneWithPreview from "components/legacy/Dropzone/DropzoneWithPreview";
import {
  UpdateCompanySettingsDefaultInterviewerImage,
  UpdateCompanySettingsDefaultInterviewerImageVariables,
  UpdateCompanySettingsLogo,
  UpdateCompanySettingsLogoVariables,
} from "generated/schemaTypes";
import Cookies from "js-cookie";
import { trackProperties } from "shared/analytics";
import { ORGANIZATION_ID_COOKIE_NAME } from "shared/constants/auth";
import { PermissionEnum } from "utils/permissions";
import useMutation from "utils/useMutation";

import styles from "./Images.module.scss";

const UPDATE_ORG_LOGO = gql`
  mutation UpdateCompanySettingsLogo($input: UpdateOrganizationLogoInput!) {
    updateOrganizationLogo(input: $input) {
      success
      code
      message
      organization {
        id
        companyLogoUrl
      }
    }
  }
`;

const UPDATE_ORG_DEFAULT_INTERVIEWER_IMAGE = gql`
  mutation UpdateCompanySettingsDefaultInterviewerImage(
    $input: UpdateOrganizationInterviewerImageInput!
  ) {
    updateOrganizationInterviewerImage(input: $input) {
      success
      code
      message
      organization {
        id
        defaultAvatarImageUrl
      }
    }
  }
`;

type ImagesProps = {
  defaultAvatarImageUrl: string;
  logoUrl: string;
};

const Images = ({ logoUrl, defaultAvatarImageUrl }: ImagesProps) => {
  const logEvent = useLogEvent({
    component: "Images",
    project: "Company branding",
  });
  const currentOrgId = Cookies.get(ORGANIZATION_ID_COOKIE_NAME);

  const { checkRolePermissions } = useAuthContext();

  const [updateLogo] = useMutation<
    UpdateCompanySettingsLogo,
    UpdateCompanySettingsLogoVariables
  >(UPDATE_ORG_LOGO);

  const [updateInterviewerImage] = useMutation<
    UpdateCompanySettingsDefaultInterviewerImage,
    UpdateCompanySettingsDefaultInterviewerImageVariables
  >(UPDATE_ORG_DEFAULT_INTERVIEWER_IMAGE);

  const updateCompanyLogo = (newUrl: string): void => {
    if (!currentOrgId) {
      // This should never happen
      console.error("Could not find organizationId");
      return;
    }
    logEvent("Company logo updated", trackProperties.isCustomerSuccess);
    updateLogo({
      variables: {
        input: {
          companyLogoUrl: newUrl,
        },
      },
    });
  };

  const updateOrganizationInterviewerImage = (newUrl: string) => {
    if (!currentOrgId) {
      // This should never happen
      console.error("Could not find organizationId");
      return;
    }
    logEvent("Default interviewer image updated");
    updateInterviewerImage({
      variables: {
        input: {
          defaultAvatarImageUrl: newUrl,
        },
      },
    });
  };

  const hasEditPermission = checkRolePermissions(
    PermissionEnum.ORGANIZATION_BRAND_WRITE
  );

  return (
    <div className="flex w-full flex-wrap gap-x-8">
      <div className="min-w-[24rem]">
        <Typography className={styles.text} variant="h3">
          Logo
        </Typography>
        <DropzoneWithPreview
          photoUrl={logoUrl || ""}
          setPhotoUrl={updateCompanyLogo}
          previewStyle="contain"
          disabled={!hasEditPermission}
        />
      </div>

      <div className="min-w-[24rem]">
        <Typography className={styles.text} variant="h3">
          Default Interviewer Image
        </Typography>
        <DropzoneWithPreview
          photoUrl={defaultAvatarImageUrl || ""}
          setPhotoUrl={updateOrganizationInterviewerImage}
          previewStyle="contain"
          disabled={!hasEditPermission}
        />
      </div>
    </div>
  );
};

export default Images;
